import React, { useEffect, useRef, useState } from "react";
import { Box, Button, FormControl, Input, InputLabel, Stack, Typography } from "@mui/material";
import Sidebar from "../elements/Sidebar";
import Header from "../elements/Header";
import AdminCompaniesTable from "../elements/Admin/AdminCompaniesTable";
import AdminUsersTable from "../elements/Admin/AdminUsersTable";
import { contentBoxStyle } from "../shared/Theme";
import companyService from "../services/companyService";
import userService from "../services/userService";
import AdminBranding from "../elements/Admin/AdminBranding";
import AdminCompanyEmployeesTable from "../elements/Admin/AdminCompanyEmployeesTable";
import DomainsTable from "../elements/Domains/DomainsTable";
import UpdateKyndModal from "../elements/Admin/UpdateKyndModal";
import { accessGuard, fileToBase64, validPassword } from "../shared/sharedFunctions";
import vscanService from "../services/vscanService";
import VscanAdminTable from "../elements/Admin/VscanAdminTable";
import alertService from "../services/alertService";

const Admin = () => {
	let [ accessLevel ] = useState(userService.AccessLevel());
	let [ selectedCompany, setSelectedCompany ] = useState(companyService.selectedCompany);
	let [ showUpdateKynd, setShowUpdateKynd ] = useState(false);
	let [ vscanUpdatedTimestamp, setVscanUpdatedTimestamp ] = useState(false);
	
	const vscanFile = useRef(null);
	const currentPassword = useRef(null);
	const newPassword = useRef(null);
	const confirmPassword = useRef(null);
	
	useEffect(() => {
		companyService.onSelectCompany(() => {
			setSelectedCompany(companyService.selectedCompany);
			setVscanUpdatedTimestamp(new Date().getTime());
		});
	}, []);
	
	const handleUpdateKyndClick = async () => {
		setShowUpdateKynd(true);
		try {
			await companyService.updateKynd();
			await companyService.selectCompany(companyService.companies[0]);
			setShowUpdateKynd(false);
		}
		catch (e) {
			console.log(e.message);
			setShowUpdateKynd(false);
		}
	};
	
	const handleUpdateBreachAwareClick = async () => {
		setShowUpdateKynd(true);
		try {
			await companyService.updateBreachAware();
			await companyService.selectCompany(companyService.companies[0]);
			setShowUpdateKynd(false);
		}
		catch (e) {
			console.log(e.message);
			setShowUpdateKynd(false);
		}
	};
	
	const handleUpdateUsecureClick = async () => {
		setShowUpdateKynd(true);
		try {
			await companyService.updateUsecure();
			await companyService.selectCompany(companyService.companies[0]);
			setShowUpdateKynd(false);
		}
		catch (e) {
			console.log(e.message);
			setShowUpdateKynd(false);
		}
	};
	
	const handleSubmitVScanForm = async (e) => {
		e.preventDefault();
		
		fileToBase64(e.target.vscanFile.files[0])
		.then(async (fileBase64) => {
			const vscanReport = {
				vscanFile: fileBase64,
				company_id: companyService.selectCompany.id
			};
			
			e.target.vscanFile.value = "";
			await vscanService.addVscanReport(vscanReport);
			setVscanUpdatedTimestamp(new Date().getTime());
			// alertService.show("VScan Report Upload Complete");
		})
		.catch((err) => {
			console.log(`ERROR: ${err}`);
		});
	};
	
	const handlePasswordChangeSubmit = async (e) => {
		e.preventDefault();
		
		let currentPass = currentPassword.current.value;
		let newPass = newPassword.current.value;
		let confirmPass = confirmPassword.current.value;
		
		if (!currentPass || currentPass === "") {
			return alertService.show("Please enter your current password");
		}
		
		if (newPass !== confirmPass) {
			if (newPassword.current) newPassword.current.value = "";
			if (confirmPassword.current) confirmPassword.current.value = "";
			
			return alertService.show("New password and confirm password do not match");
		}
		
		if (!validPassword(newPass)) {
			if (newPassword.current) newPassword.current.value = "";
			if (confirmPassword.current) confirmPassword.current.value = "";
			
			let msg = `Invalid password\n`;
			msg += `Password must be at least 8 characters and contain at least 1 uppercase, 1 lowercase,`;
			msg += `1 number and 1 special character`;
			return alertService.show(msg);
		}
		
		try {
			await userService.changePassword(currentPass, newPass, confirmPass);
		}
		catch (exception) {
			if (currentPassword.current) currentPassword.current.value = "";
			
			return alertService.show("Error updating password");
		}
		
		if (currentPassword.current) currentPassword.current.value = "";
		if (newPassword.current) newPassword.current.value = "";
		if (confirmPassword.current) confirmPassword.current.value = "";
		return alertService.show("Password updated. You have been signed out on any other devices.");
	};
	
	return (
		<Box sx={{ display: "flex" }}>
			<Sidebar/>
			<Box component="main" sx={{flexGrow: 1, bgcolor: "background.default"}}>
				<Header pageTitle="Admin"/>
				{(accessLevel === 1 || accessLevel === 2) &&
					<React.Fragment>
						<Box sx={contentBoxStyle}>
							<AdminCompaniesTable/>
							<AdminUsersTable company={selectedCompany}/>
							<DomainsTable/>
							<AdminCompanyEmployeesTable company={selectedCompany}/>
							<Stack direction="row" sx={{ width: "80%", margin: "1em auto" }}>
								<AdminBranding/>
								{accessGuard([1])?
									<Box sx={{ width: "50%", borderLeft: "solid 2px" }}>
										<Stack direction="column" spacing="2em">
											<Stack sx={{ width: "80%", textAlign: "center", margin: "auto" }}>
												<Typography variant="h6" sx={{ width: "100%", margin: "1em 0" }}>
													Miscellaneous Settings
												</Typography>
												<Button
													onClick={handleUpdateKyndClick}
													sx={{ width: "100%", marginTop: "0.5em" }}
												>
													Update Attack Surface
												</Button>
												<Button
													onClick={handleUpdateBreachAwareClick}
													sx={{ width: "100%", marginTop: "0.5em" }}
												>
													Update Dark Web
												</Button>
												<Button
													onClick={handleUpdateUsecureClick}
													sx={{ width: "100%", marginTop: "0.5em" }}
												>
													Update Cyber Training
												</Button>
											</Stack>
										</Stack>
									</Box>
									: <React.Fragment/>
								}
							</Stack>
							<Stack
								direction="column"
								alignItems="center"
								justifyContent="center"
								spacing="2em"
								sx={{ width: "80%", margin: "2em auto" }}
							>
								<Typography sx={{ width: "100%", textAlign: "center" }}>
									Vulnerability Scan Reports
								</Typography>
								<VscanAdminTable updatedTimestamp={vscanUpdatedTimestamp}/>
								<Typography sx={{ width: "100%", textAlign: "center" }}>
									Upload Vulnerability Scan Report
								</Typography>
								<form
									id="vscanForm"
									onSubmit={handleSubmitVScanForm}
									autoComplete="off"
								>
									<FormControl sx={{
										width: "100%",
										maxWidth: "35em",
										margin: "1em auto",
										display: "flex",
										lexDirection: "row",
										flexGrow: 1
									}}>
										<Input
											inputRef={vscanFile}
											id="vscanFile"
											type="file"
											autoComplete="off"
											defaultValue=""
										/>
									</FormControl>
								</form>
								<Button
									sx={{
										width: "100%",
										maxWidth: "35em",
										textAlign: "center",
										margin: "auto"
									}}
									type="submit"
									form="vscanForm"
									autoFocus
								>
									Submit
								</Button>
							</Stack>
						</Box>
						<UpdateKyndModal
							open={showUpdateKynd}
						/>
					</React.Fragment>
				}
				<Typography sx={{ width: "100%", textAlign: "center" }}>
					Change Account Password
				</Typography>
				<form
					id="passwordForm"
					onSubmit={handlePasswordChangeSubmit}
					autoComplete="off"
				>
					<FormControl sx={{
						width: "100%",
						maxWidth: "35em",
						margin: "1em auto",
						display: "flex",
						lexDirection: "row",
						flexGrow: 1
					}}>
						<InputLabel htmlFor="password">Current Password</InputLabel>
						<Input inputRef={currentPassword} id="currentpassword" type="password"/>
					</FormControl>
					<FormControl sx={{
						width: "100%",
						maxWidth: "35em",
						margin: "1em auto",
						display: "flex",
						lexDirection: "row",
						flexGrow: 1
					}}>
						<InputLabel htmlFor="password">New Password</InputLabel>
						<Input inputRef={newPassword} id="newpassword" type="password"/>
					</FormControl>
					<FormControl sx={{
						width: "100%",
						maxWidth: "35em",
						margin: "1em auto",
						display: "flex",
						lexDirection: "row",
						flexGrow: 1
					}}>
						<InputLabel htmlFor="password">Confirm New Password</InputLabel>
						<Input inputRef={confirmPassword} id="confirmpassword" type="password"/>
					</FormControl>
					<FormControl sx={{
						width: "100%",
						maxWidth: "35em",
						margin: "1em auto",
						display: "flex",
						lexDirection: "row",
						flexGrow: 1
					}}>
						<Button
							sx={{
								width: "100%",
								maxWidth: "35em",
								textAlign: "center",
								margin: "auto"
							}}
							type="submit"
							form="passwordForm"
						>
							Submit
						</Button>
					</FormControl>
				</form>
			</Box>
		</Box>
	);
}

export default Admin;